import { hoverDynamic } from './hover';
import { setCookie } from './_cookie';
/**
 * ScatteredCards 機能を初期化するエントリーポイントの関数
 * 
 * ページに .scattered-cards-container が存在する場合に
 * ScatteredCardsManager クラスのインスタンスを作成し、カードの生成や動きの管理を行います。
 * 
 */
export function scatteredCards() {
  // =====================
  // 1) デフォルト設定
  // =====================
  /**
   * 全体で用いる設定のデフォルト値を定義したオブジェクト。
   * 動作モードやレイアウト設定、マージンやカードサイズなど細かなパラメータを持つ。
   */
  const defaultConfig = {
    // ===== 共通設定 =====
    shuffle: true,          // カードをシャッフルして表示するかどうか。
    initialAreaFactor: 3,   // 初期配置時の散らばり具合を決める係数
    maxIterations: 400,     // 衝突回避計算の最大反復回数
    forceStrength: 0.2,     // 衝突時に反発させる力の強さ
    collisionThreshold: 0,  // 衝突閾値(重なりをどれだけ許容するか)
    minCardPadding: 1.5,
    maxCardPadding: 2,
    minSizeRatio: 2,
    maxSizeRatio: 20,

    /**
     * 縦書き用カードに関する設定。
     * カードの幅を minRem～maxRem に収める想定。
     * remPerChar * 文字数で幅を計算する。
     */
    horizontalText: {
      maxRem: 20,
      remPerChar: 2
    },

    /**
     * 縦書き用カードに関する設定。
     * ratio で縦書きになる確率を指定し、
     * カードの高さを minRem～maxRem に収める想定。
     * remPerChar * 文字数で高さを計算する。
     */
    verticalText: {
      ratio: 0.3,
      maxRem: 20,
      remPerChar: 2
    },

    /**
     * カードエリアの移動に関する設定。
     * マウス操作やオートプレイでコンテンツエリアがどのように動くかを決める。
     */
    movement: {
      autoplay: true,     // カードエリアの自動移動（オートプレイ）を開始時点で有効にするかどうか。
      maxSpeed: 4,        // 最大速度
      minSpeed: 0.5,      // 最小速度
      acceleration: 0.2,  // 加速度
      deceleration: 0.1,  // 減速度
      deadzone: 0.1,      // マウス/スティック操作のデッドゾーン(これ以下なら速度0とみなす)
      invertX: true,      // マウスX操作を反転させるかどうか
      invertY: true,      // マウスY操作を反転させるかどうか
      autoplaySpeed: 0.7  // オートプレイ時の移動速度
    },

    /**
     * 一時的に処理を停止する（ポーズ）条件となるクラス名の配列。
     * bodyタグにこれらのクラスがついている場合、アニメーションなどを停止する。
     */
    pauseOnClasses: ["is-nav-active", "is-search-active"]
  };

  // =====================
  // 2) モバイル向け上書き設定
  // =====================
  /**
   * 画面幅が小さいデバイス（モバイル）の場合に、defaultConfig から一部の値を上書きする。
   */
  const mobileOverrides = {
    initialAreaFactor: 4.5,
    minCardPadding: 1,
    maxCardPadding: 1.5,
    minSizeRatio: 2,
    maxSizeRatio: 10,
    horizontalText: {
      maxRem: 15,
      remPerChar: 2
    },
    verticalText: {
      maxRem: 15,
      remPerChar: 2
    },
    movement: {
      maxSpeed: 3,
      autoplaySpeed: 0.5
    }
  };

  /**
   * オブジェクト同士を再帰的にマージするヘルパー関数。
   * 配列以外のネストされたオブジェクトを上書きする。
   */
  function mergeDeep(target, source) {
    for (const key in source) {
      if (!Object.prototype.hasOwnProperty.call(source, key)) continue;
      const val = source[key];
      // val がオブジェクトの場合は再帰的にマージ
      if (typeof val === "object" && val !== null && !Array.isArray(val)) {
        if (!target[key]) target[key] = {};
        mergeDeep(target[key], val);
      } else {
        // それ以外は単純上書き
        target[key] = val;
      }
    }
  }

  /**
   * ウィンドウサイズを元に、defaultConfig と mobileOverrides を統合した最終的な設定オブジェクトを返す。
   */
  function getMergedConfig() {
    // デフォルトを複製
    const config = JSON.parse(JSON.stringify(defaultConfig));
    // 画面幅が768以下なら mobileOverrides をマージ
    if (window.innerWidth <= 768) {
      mergeDeep(config, mobileOverrides);
    }
    return config;
  }

  // ============================
  // 3) ScatteredCardsManager クラス
  // ============================
  /**
   * カードの生成や、カードを含むコンテンツ全体の移動制御、衝突回避レイアウトなどを統括するクラス。
   * scatteredCards() の中でインスタンス化され、イベントリスナーやアニメーションの初期化を行う。
   */
  class ScatteredCardsManager {
    constructor() {
      // .scattered-cards-container 要素を取得（カード表示領域）
      this.container = document.querySelector(".scattered-cards-container");

      // イベントリスナーで使用するメソッドをバインド
      this.boundHandleMouseMove = this.handleMouseMove.bind(this);
      this.boundHandleResize = this.handleResize.bind(this);
      this.boundHandlePlaybackControl = this.handlePlaybackControl.bind(this);

      // ポインターがcoarse（指など）か、maxTouchPointsがある場合はタッチデバイスとみなす
      this.isTouchDevice = window.matchMedia("(pointer: coarse)").matches || navigator.maxTouchPoints > 0;

      // 各種初期化処理
      this.initConfig();         // 設定を取得
      this.initElements();       // 必要なDOM要素を取得
      this.initState();          // 状態管理用のオブジェクトを初期化
      this.initPauseObserver();  // bodyクラス監視で一時停止する機能を初期化
      this.attachEventListeners(); // イベントリスナーの登録
      this.initialize();         // カード生成＆レイアウトなどの初期化

      // タッチデバイスならバーチャルスティック（joystickUI）も初期化
      if (this.isTouchDevice) {
        this.initJoystickUI();
      }
    }

    /**
     * 実際に最終的な設定を合成して this.config に保存する。
     */
    initConfig() {
      this.config = getMergedConfig();
    }

    /**
     * container 内から特定の要素（.scattered-cards-content や .playback-control）を取得し、
     * それらをまとめるオブジェクトを this.elements に保持する。
     */
    initElements() {
      const container = this.container;
      this.elements = {
        content: container.querySelector(".scattered-cards-content"),  // カードを入れるコンテンツ領域
        playbackControl: container.querySelector(".playback-control")  // 再生/停止ボタン
      };
      // 要素の存在確認。あるかどうかをbooleanで管理
      this.elementExists = {
        content: !!this.elements.content,
        playbackControl: !!this.elements.playbackControl
      };
      // カード要素を配列で保持するための初期化
      this.cardElements = [];
    }

    /**
     * コンテンツの動きやアニメーション状態などを管理するための state オブジェクトを初期化。
     */
    initState() {
      this.state = {
        mousePosition: { x: 0, y: 0 }, // マウスまたはスティックの入力（-1～1程度）
        bounds: null,                 // カードの配置全体を囲む境界
        rafId: null,                  // requestAnimationFrame の ID
        viewportCenter: { x: window.innerWidth / 2, y: window.innerHeight / 2 }, // 画面中央座標

        // autoplay に関する状態
        autoplay: {
          isPlaying: false,     // オートプレイが有効かどうか
          angle: Math.random() * Math.PI * 2, // 移動方向を決める角度(ランダム初期値)
          velocity: { x: 0, y: 0 }, // オートプレイ時の移動速度ベクトル
          position: { x: 0, y: 0 }  // オートプレイ時にコンテンツが移動している座標
        },

        // マウス操作での移動に関する状態
        movement: {
          velocity: { x: 0, y: 0 },      // 現在の移動速度
          currentPosition: { x: 0, y: 0 } // コンテンツの現在の平行移動量
        },

        // 一時停止（ポーズ）に関する状態
        pause: {
          isPaused: false,      // 一時停止中かどうか
          wasAutoPlaying: false // 一時停止時にオートプレイ中だったかどうか
        }
      };
    }

    /**
     * body 要素のクラス変化を監視し、pauseOnClasses に含まれるクラスがつけばアニメーションを停止、
     * クラスが外れれば再開する機能を持つ MutationObserver を初期化する。
     */
    initPauseObserver() {
      const bodyElement = document.body;
      this.pauseObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.attributeName === "class") {
            // body のクラスが変化したらハンドラを呼ぶ
            this.handleBodyClassChange();
          }
        });
      });
      // body のクラス属性を監視
      this.pauseObserver.observe(bodyElement, {
        attributes: true,
        attributeFilter: ["class"]
      });
      // 初期状態でもクラスをチェック
      this.handleBodyClassChange();
    }

    /**
     * 各種イベントリスナーを登録する。
     * - マウス移動
     * - リサイズ
     * - 再生/停止ボタンクリック
     * - アニメーション開始
     */
    attachEventListeners() {
      if (!this.isTouchDevice) {
        // タッチデバイスでない場合のみマウス移動イベントを監視
        window.addEventListener("mousemove", this.boundHandleMouseMove);
      }
      // 画面リサイズ
      window.addEventListener("resize", this.boundHandleResize);

      // 再生/停止ボタン
      if (this.elementExists.playbackControl) {
        this.elements.playbackControl.addEventListener("click", this.boundHandlePlaybackControl);
      }
      // アニメーション開始
      this.startAnimation();
    }

    /**
     * 初期化時に行う具体的な処理。
     * - コンテンツの中心配置
     * - カード生成
     * - カードのサイズ計測＆レイアウト適用
     * - コンテンツ領域のバウンディング（bounds）計算
     * - オートプレイ初期化
     */
    initialize() {
      this.centerContent();            // コンテンツを画面中央に配置
      this.createCards();              // カード生成
      this.measureCardsAndLayout();    // カードサイズを測りレイアウト処理
      this.calculateContentBounds();   // コンテンツ全体のバウンディング計算
      this.initializePlaybackState();  // オートプレイ設定を反映
    }

    /**
     * タッチデバイス時に表示するバーチャルジョイスティック操作で mousePosition に値を入れる。
     */
    initJoystickUI() {
      // ジョイスティックのベース
      const joystickBase = document.querySelector('.joystick-base')

      // 操作用のハンドル
      const joystickHandle = document.querySelector('.joystick-handle')

      if(!joystickBase && !joystickHandle) {
        return;
      }

      // 操作時のフラグや中心座標
      let isTouching = false;
      let center = { x: 0, y: 0 };

      // タッチ開始時の処理
      joystickBase.addEventListener("touchstart", (e) => {
        e.preventDefault();
        setCookie('smls-joystickTooltip', '1', 2592000);
        document.documentElement.classList.add('is-no-joystick-tooltip');
        isTouching = true;
        const rect = joystickBase.getBoundingClientRect();
        // ベースの中心を計算
        center.x = rect.width / 2;
        center.y = rect.height / 2;
      });

      // タッチ移動時の処理
      joystickBase.addEventListener("touchmove", (e) => {
        if (!isTouching) return;
        e.preventDefault();
        const rect = joystickBase.getBoundingClientRect();
        // タッチ座標をベースの左上基準で計算
        const baseX = e.touches[0].clientX - rect.left;
        const baseY = e.touches[0].clientY - rect.top;
        const deltaX = baseX - center.x;
        const deltaY = baseY - center.y;

        // ハンドルを動かせる最大半径
        const maxRadius = 50;

        // 実際の距離を計算し、maxRadiusを超えたら制限
        let dist = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
        if (dist > maxRadius) dist = maxRadius;

        // 角度を求め、正規化した位置を計算
        const angle = Math.atan2(deltaY, deltaX);
        const normX = dist * Math.cos(angle);
        const normY = dist * Math.sin(angle);

        // ハンドル位置をCSS transformで更新
        joystickHandle.style.transform = `
          translate(-50%, -50%)
          translate(${normX}px, ${normY}px)
        `;

        // [-1, 1]の範囲に正規化（maxRadiusで割る）
        const rangeX = normX / maxRadius;
        const rangeY = normY / maxRadius;

        // invertX, invertYを考慮して state.mousePosition に反映
        const invertedX = this.config.movement.invertX ? -rangeX : rangeX;
        const invertedY = this.config.movement.invertY ? -rangeY : rangeY;
        this.state.mousePosition.x = invertedX;
        this.state.mousePosition.y = invertedY;
      });

      // タッチ終了時
      joystickBase.addEventListener("touchend", (e) => {
        e.preventDefault();
        isTouching = false;
        // ハンドルを中心に戻す
        joystickHandle.style.transform = `translate(-50%, -50%)`;
        // マウス位置も0に戻す
        this.state.mousePosition.x = 0;
        this.state.mousePosition.y = 0;
      });
    }

    /**
     * カード領域 (.scattered-cards-content) を画面中央に固定するためのスタイルを適用。
     */
    centerContent() {
      if (!this.elementExists.content) return;
      const { content } = this.elements;
      content.style.left = "50%";
      content.style.top = "50%";
      content.style.transform = "translate(-50%, -50%)";
    }

    /**
     * カード生成ロジック。
     * window.cardSetting?.cards を元に、title / sentences / keywords をそれぞれ展開し、
     * createCardElement() で要素を作って this.cardElements に追加する。
     */
    createCards() {
      // window.cardSetting.cards が存在し、かつ content要素が存在する場合のみ実行
      if (!window.cardSetting?.cards || !this.elementExists.content) return;

      // 既存カードを一旦削除
      this.cardElements.forEach((card) => card.element.remove());
      this.cardElements = [];

      // カード生成用の配列
      let allCardData = [];

      // cardSetting.cards を展開
      window.cardSetting.cards.forEach((cardInfo) => {
        // 2) sentences（配列）をカード化
        if (Array.isArray(cardInfo.sentences)) {
          cardInfo.sentences.forEach((sentence) => {
            allCardData.push({
              text: sentence,
              permalink: cardInfo.permalink
            });
          });
        }
        // 3) keywords（配列）をカード化
        if (Array.isArray(cardInfo.keywords)) {
          cardInfo.keywords.forEach((keyword) => {
            allCardData.push({
              text: keyword,
              permalink: cardInfo.permalink
            });
          });
        }
      });

      // シャッフル設定が有効なら配列をシャッフル
      if (this.config.shuffle) {
        allCardData = this.shuffleArray(allCardData);
      }

      // カード要素を生成してDOMに追加
      allCardData.forEach((item) => {
        const card = this.createCardElement(item.text, item.permalink);
        this.elements.content.appendChild(card.element);
        this.cardElements.push(card);
      });
    }

    /**
     * カードのサイズを測定し、位置を決定する。
     */
    measureCardsAndLayout() {
      // まず全カードの要素から実際の幅・高さを取得
      this.cardElements.forEach((card) => {
        const rect = card.element.getBoundingClientRect();
        card.width = rect.width;
        card.height = rect.height;
      });

      // 最大幅・最大高さをいったん計測（特に使わないが、必要に応じて参照可）
      let maxW = 0;
      let maxH = 0;
      this.cardElements.forEach((c) => {
        if (c.width > maxW) maxW = c.width;
        if (c.height > maxH) maxH = c.height;
      });
      this.maxCardWidth = maxW;
      this.maxCardHeight = maxH;

      // 1) ランダム配置
      this.randomlyPlaceCards();
      // 2) 衝突を回避するアルゴリズムを実行
      this.resolveCollisionsForceBased();

      // 最終的に配置が完了した状態で再度コンテンツ全体のバウンディングを取得
      this.calculateContentBounds();
    }

    /**
     * 初期配置としてランダムにカードを配置する。
     */
    randomlyPlaceCards() {
      const cards = this.cardElements;

      // カード枚数と文字数の合計をざっくり算出
      const cardCount = cards.length;
      const totalChars = cards.reduce((acc, c) => {
        // カード要素のテキスト長を加算
        return acc + (c.element.textContent?.length || 0);
      }, 0);

      // initialAreaFactor に基づき、カード数や文字数に応じてエリア拡大係数を調整
      const baseFactor = this.config.initialAreaFactor;
      let factor = baseFactor;

      // カード枚数が多いほど sqrt()で拡大
      factor *= Math.sqrt(Math.max(1, cardCount) / 1000);
      // 文字数が多いほど log10()で少しだけ拡大
      factor *= 1 + Math.log10(Math.max(1, totalChars + 1)) / 2;

      // ウィンドウサイズ * factor で散らばる範囲を決定
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const rangeW = vw * factor;
      const rangeH = vh * factor;
      const halfW = rangeW / 2;
      const halfH = rangeH / 2;

      // 各カードをランダム座標に配置
      cards.forEach((card) => {
        const rx = Math.random() * rangeW - halfW;
        const ry = Math.random() * rangeH - halfH;
        card.position = { x: rx, y: ry };
        // transform で位置を反映
        card.element.style.transform = `
          translate(-50%, -50%)
          translate(${rx}px, ${ry}px)
        `;
      });
    }

    /**
     * 衝突回避。
     * 衝突しているカード同士を反発させる処理を繰り返し、重なりを減らす。
     */
    resolveCollisionsForceBased() {
      const { maxIterations, forceStrength, collisionThreshold } = this.config;

      let iteration = 0;
      while (iteration < maxIterations) {
        let movedCount = 0;

        // 2重ループで全カードペアをチェック
        for (let i = 0; i < this.cardElements.length; i++) {
          for (let j = i + 1; j < this.cardElements.length; j++) {
            const a = this.cardElements[i];
            const b = this.cardElements[j];

            // 衝突しているかどうか判定
            if (this.checkOverlap(a, b, collisionThreshold)) {
              movedCount++;
              // 衝突していれば反発（位置をずらす）
              this.applyRepulsion(a, b, forceStrength);
            }
          }
        }

        iteration++;
        // 衝突回避後の位置をDOMに反映
        this.cardElements.forEach((card) => {
          card.element.style.transform = `
            translate(-50%, -50%)
            translate(${card.position.x}px, ${card.position.y}px)
          `;
        });

        // 何も動かなかったら終了
        if (movedCount === 0) {
          break;
        }
      }

      // 最後にカード全体を中心に寄せる
      this.centerForceBasedCards();
    }

    /**
     * 衝突回避後に、カード全体が画面のどこかに偏ってしまうことがあるため、
     * その重心を求めて中央に配置し直す。
     */
    centerForceBasedCards() {
      let minX = Infinity;
      let maxX = -Infinity;
      let minY = Infinity;
      let maxY = -Infinity;

      // 各カードの外周を見て最大・最小を更新
      this.cardElements.forEach((card) => {
        const left = card.position.x - card.width / 2;
        const right = card.position.x + card.width / 2;
        const top = card.position.y - card.height / 2;
        const bottom = card.position.y + card.height / 2;

        if (left < minX) minX = left;
        if (right > maxX) maxX = right;
        if (top < minY) minY = top;
        if (bottom > maxY) maxY = bottom;
      });

      // 全体の中心座標
      const centerX = (minX + maxX) / 2;
      const centerY = (minY + maxY) / 2;

      // 全カードの座標を中心基準に再調整
      this.cardElements.forEach((card) => {
        card.position.x -= centerX;
        card.position.y -= centerY;

        card.element.style.transform = `
          translate(-50%, -50%)
          translate(${card.position.x}px, ${card.position.y}px)
        `;
      });
    }

    /**
     * 2つのカードが閾値を超えて重なっているかどうか判定する。
     * @param {*} a カードA
     * @param {*} b カードB
     * @param {*} threshold 衝突閾値
     */
    checkOverlap(a, b, threshold) {
      const ax1 = a.position.x - a.width / 2;
      const ax2 = a.position.x + a.width / 2;
      const ay1 = a.position.y - a.height / 2;
      const ay2 = a.position.y + a.height / 2;

      const bx1 = b.position.x - b.width / 2;
      const bx2 = b.position.x + b.width / 2;
      const by1 = b.position.y - b.height / 2;
      const by2 = b.position.y + b.height / 2;

      // いずれかが離れていれば衝突していない
      return !(
        ax2 < bx1 - threshold ||
        ax1 > bx2 + threshold ||
        ay2 < by1 - threshold ||
        ay1 > by2 + threshold
      );
    }

    /**
     * 2つのカードが重なっている時に反発力を適用して位置をずらす。
     * @param {*} a カードA
     * @param {*} b カードB
     * @param {*} forceStrength 反発力
     */
    applyRepulsion(a, b, forceStrength) {
      const dx = b.position.x - a.position.x;
      const dy = b.position.y - a.position.y;
      const dist = Math.sqrt(dx * dx + dy * dy);

      // 同一座標に重なりすぎの場合、ランダムに少し動かしておく
      if (dist === 0) {
        b.position.x += (Math.random() - 0.5) * 1;
        b.position.y += (Math.random() - 0.5) * 1;
        return;
      }

      // x, y方向の重なり幅を計算
      const overlapX = (a.width + b.width) / 2 - Math.abs(dx);
      const overlapY = (a.height + b.height) / 2 - Math.abs(dy);
      const overlap = overlapX > 0 && overlapY > 0 ? Math.min(overlapX, overlapY) : 0;

      // 距離方向の単位ベクトル
      const ux = dx / dist;
      const uy = dy / dist;

      // 反発量
      const repulsion = overlap * forceStrength;

      // AとBに反対方向の力をかける
      a.position.x -= (repulsion / 2) * ux;
      a.position.y -= (repulsion / 2) * uy;
      b.position.x += (repulsion / 2) * ux;
      b.position.y += (repulsion / 2) * uy;
    }

    /**
     * カード全体を囲むバウンディングボックス(bounds)を算出し、
     * this.state.bounds に保存する。
     */
    calculateContentBounds() {
      this.state.bounds = this.cardElements.reduce(
        (bounds, card) => {
          const x = card.position.x;
          const y = card.position.y;
          const halfW = card.width / 2;
          const halfH = card.height / 2;
          return {
            left: Math.min(bounds.left, x - halfW),
            right: Math.max(bounds.right, x + halfW),
            top: Math.min(bounds.top, y - halfH),
            bottom: Math.max(bounds.bottom, y + halfH)
          };
        },
        { left: Infinity, right: -Infinity, top: Infinity, bottom: -Infinity }
      );

      this.state.bounds.left;
      this.state.bounds.right;
      this.state.bounds.top;
      this.state.bounds.bottom;
    }

    /**
     * オートプレイを設定通りに初期化し、再生中であれば速度ベクトルを計算する。
     */
    initializePlaybackState() {
      if (!this.elementExists.playbackControl) return;
      // config.movement.autoplay に応じて再生状態を決定
      this.state.autoplay.isPlaying = this.config.movement.autoplay;
      if (this.state.autoplay.isPlaying) {
        const { angle } = this.state.autoplay;
        const speed = this.config.movement.autoplaySpeed;
        this.state.autoplay.velocity = {
          x: Math.cos(angle) * speed,
          y: Math.sin(angle) * speed
        };
      }
      this.updatePlaybackControlState();
    }

    /**
     * body のクラス変更を検知した際に呼ばれ、pauseOnClasses に該当するクラスが付いていればアニメーション停止、
     * なければ再開する。
     */
    handleBodyClassChange() {
      const bodyElement = document.body;
      const shouldPause = this.config.pauseOnClasses.some((className) =>
        bodyElement.classList.contains(className)
      );
      if (shouldPause && !this.state.pause.isPaused) {
        this.pauseAnimation();
      } else if (!shouldPause && this.state.pause.isPaused) {
        this.resumeAnimation();
      }
    }

    /**
     * アニメーションを一時停止する。
     * すでに停止中なら何もしない。
     * 停止時には、オートプレイが動いていたかどうかを記憶しておき、再開時に復元する。
     */
    pauseAnimation() {
      if (this.state.pause.isPaused) return;
      this.state.pause.isPaused = true;
      this.state.pause.wasAutoPlaying = this.state.autoplay.isPlaying;
      // オートプレイ中なら停止処理
      if (this.state.autoplay.isPlaying) {
        this.state.autoplay.isPlaying = false;
        // 停止時点の座標を movement.currentPosition に保存
        const lastPosition = { ...this.state.autoplay.position };
        this.state.movement.currentPosition = lastPosition;
        this.state.movement.velocity = { x: 0, y: 0 };
      }
      // ポーズ中は再生ボタンを押せないように見た目を変える（オプション）
      if (this.elementExists.playbackControl) {
        this.elements.playbackControl.style.pointerEvents = "none";
        this.elements.playbackControl.style.opacity = "0.5";
      }
    }

    /**
     * アニメーションを再開する。
     * 一時停止中でない場合は何もしない。
     * 再開時に、停止前がオートプレイなら再度オートプレイを開始する。
     */
    resumeAnimation() {
      if (!this.state.pause.isPaused) return;
      this.state.pause.isPaused = false;
      // ポーズ前にオートプレイしていた場合は再開
      if (this.state.pause.wasAutoPlaying) {
        this.state.autoplay.isPlaying = true;
        this.state.autoplay.angle = Math.random() * Math.PI * 2;
        const speed = this.config.movement.autoplaySpeed;
        this.state.autoplay.velocity = {
          x: Math.cos(this.state.autoplay.angle) * speed,
          y: Math.sin(this.state.autoplay.angle) * speed
        };
      }
      // 見た目を元に戻す（オプション）
      if (this.elementExists.playbackControl) {
        this.elements.playbackControl.style.pointerEvents = "";
        this.elements.playbackControl.style.opacity = "";
      }
    }

    /**
     * 再生/停止ボタンをクリックした時の処理。
     * オートプレイがONならOFFに、OFFならONに切り替える。
     */
    handlePlaybackControl() {
      const wasPlaying = this.state.autoplay.isPlaying;
      this.state.autoplay.isPlaying = !wasPlaying;
      if (!wasPlaying) {
        // 再生開始する場合
        this.state.autoplay.position = { ...this.state.movement.currentPosition };
        this.state.autoplay.angle = Math.random() * Math.PI * 2;
        const speed = this.config.movement.autoplaySpeed;
        this.state.autoplay.velocity = {
          x: Math.cos(this.state.autoplay.angle) * speed,
          y: Math.sin(this.state.autoplay.angle) * speed
        };
      } else {
        // 再生中なら停止にする
        this.state.autoplay.isPlaying = false;
        this.state.movement.currentPosition = { ...this.state.autoplay.position };
        this.state.movement.velocity = { x: 0, y: 0 };
      }
      this.updatePlaybackControlState();
    }

    /**
     * 再生/停止ボタンの見た目を is-playing クラスの有無で制御。
     */
    updatePlaybackControlState() {
      if (!this.elementExists.playbackControl) return;
      const { playbackControl } = this.elements;
      if (this.state.autoplay.isPlaying) {
        document.body.classList.add("is-card-playing");
      } else {
        document.body.classList.remove("is-card-playing");
      }
    }

    /**
     * アニメーションループを開始し、フレームごとに updateCards, updateContentPosition を呼ぶ。
     */
    startAnimation() {
      const animate = () => {
        this.updateCards();            // カード個別のアップデート（スケールやパララックスなど）
        this.updateContentPosition();  // コンテンツ（カード全体）の位置を更新
        this.state.rafId = requestAnimationFrame(animate);
      };
      this.state.rafId = requestAnimationFrame(animate);
    }

    /**
     * 各カードのアップデート処理（dynamicScale など）を行う。
     */
    updateCards() {
      if (!this.cardElements.length) return;
      const { mousePosition } = this.state;

      this.cardElements.forEach((card) => {
        const baseX = card.position.x;
        const baseY = card.position.y;

        // ここで parallaxAmount 等を計算するなら offset に加算できるが、
        // 現在は 0 としている
        const distance = 0;
        const offset = distance;
        const offsetX = baseX + mousePosition.x * offset;
        const offsetY = baseY + mousePosition.y * offset;

        let transformStyle = `
          translate(-50%, -50%)
          translate(${offsetX}px, ${offsetY}px)
        `;

        card.element.style.transform = transformStyle;
      });
    }

    /**
     * カードコンテナ（.scattered-cards-content）の位置を更新する。
     * オートプレイがONならオートプレイ位置を優先し、OFFならマウス入力による移動。
     */
    updateContentPosition() {
      if (!this.state.bounds || this.state.pause.isPaused) return;

      // オートプレイ中ならそちらを優先
      if (this.state.autoplay.isPlaying) {
        this.updateAutoplayPosition();
        this.elements.content.style.transform = `
          translate(-50%, -50%)
          translate(${this.state.autoplay.position.x}px, ${this.state.autoplay.position.y}px)
        `;
        return;
      }

      // 通常のマウス操作
      const { mousePosition, movement } = this.state;
      const { bounds } = this.state;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const { maxSpeed, minSpeed, acceleration, deceleration, deadzone } = this.config.movement;

      // マウス入力（-1～1）から targetVelocity を計算
      const calcTargetVelocity = (pos) => {
        const absPos = Math.abs(pos);
        // deadzone 以下なら速度0
        if (absPos < deadzone) return 0;
        // deadzone を外れた範囲を [0,1] に正規化
        const norm = (absPos - deadzone) / (1 - deadzone);
        // minSpeed から maxSpeed の間を補間
        return Math.sign(pos) * (minSpeed + (maxSpeed - minSpeed) * norm);
      };

      // 現在速度 current を target へ補間（加速度・減速度を考慮）
      const updateVelocity = (current, target) => {
        const diff = target - current;
        if (Math.abs(diff) < 0.01) return target; // 誤差が小さければターゲットとみなす
        const change = Math.abs(diff) * (target !== 0 ? acceleration : deceleration);
        return current + Math.sign(diff) * Math.min(Math.abs(diff), change);
      };

      // x, y の目標速度を計算
      const targetVelX = calcTargetVelocity(mousePosition.x);
      const targetVelY = calcTargetVelocity(mousePosition.y);

      // 現在速度を少しずつターゲットに近づける
      movement.velocity.x = updateVelocity(movement.velocity.x, targetVelX);
      movement.velocity.y = updateVelocity(movement.velocity.y, targetVelY);

      // bounds を超えないように制限をかけながら移動
      const maxX = Math.max(0, bounds.right - vw / 2);
      const maxY = Math.max(0, bounds.bottom - vh / 2);

      movement.currentPosition.x = Math.max(
        -maxX,
        Math.min(maxX, movement.currentPosition.x + movement.velocity.x)
      );
      movement.currentPosition.y = Math.max(
        -maxY,
        Math.min(maxY, movement.currentPosition.y + movement.velocity.y)
      );

      // スタイル反映
      this.elements.content.style.transform = `
        translate(-50%, -50%)
        translate(${movement.currentPosition.x}px, ${movement.currentPosition.y}px)
      `;
    }

    /**
     * オートプレイ時の座標更新。
     * 端に達したらバウンドさせる。
     */
    updateAutoplayPosition() {
      if (!this.state.autoplay.isPlaying || this.state.pause.isPaused) return;
      const { bounds } = this.state;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const maxX = Math.max(0, bounds.right - vw / 2);
      const maxY = Math.max(0, bounds.bottom - vh / 2);

      const nextX = this.state.autoplay.position.x + this.state.autoplay.velocity.x;
      const nextY = this.state.autoplay.position.y + this.state.autoplay.velocity.y;

      // バウンド処理: 範囲外に出そうになったら速度を反転
      if (Math.abs(nextX) > maxX) {
        this.state.autoplay.velocity.x *= -1;
      }
      if (Math.abs(nextY) > maxY) {
        this.state.autoplay.velocity.y *= -1;
      }

      // 座標を更新（範囲内にクリップ）
      this.state.autoplay.position.x = Math.max(-maxX, Math.min(maxX, nextX));
      this.state.autoplay.position.y = Math.max(-maxY, Math.min(maxY, nextY));
    }

    /**
     * マウスが動いたときの処理。
     * オートプレイ中や一時停止中なら無視。
     * マウス位置を [-1, 1] に正規化し、invertX, invertY が true なら反転する。
     */
    handleMouseMove(e) {
      if (this.state.pause.isPaused || this.state.autoplay.isPlaying) return;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      // 画面中央を(0,0)としたときの比率に変換
      const rawX = (e.clientX - vw / 2) / (vw / 2);
      const rawY = (e.clientY - vh / 2) / (vh / 2);

      this.state.mousePosition = {
        x: this.config.movement.invertX ? -rawX : rawX,
        y: this.config.movement.invertY ? -rawY : rawY
      };
      this.state.viewportCenter = { x: vw / 2, y: vh / 2 };
    }

    /**
     * 画面リサイズ時の処理。
     * アニメーションを一旦キャンセルし、少し待ってから再初期化・再開する。
     */
    handleResize() {
      if (this.state.rafId) {
        cancelAnimationFrame(this.state.rafId);
        this.state.rafId = null;
      }
      clearTimeout(this.resizeTimeout);

      // 200ms程度待ってから再初期化（リサイズ連打による負荷を軽減）
      this.resizeTimeout = setTimeout(() => {
        this.state.viewportCenter = { x: window.innerWidth / 2, y: window.innerHeight / 2 };
        // 設定を再取得
        this.config = getMergedConfig();

        // 旧の位置を記憶（オートプレイ中か否かで参照先が違う）
        const oldPosition = this.state.autoplay.isPlaying
          ? this.state.autoplay.position
          : this.state.movement.currentPosition;

        // カードなどを再生成
        this.initialize();

        // 新しいレイアウトにもとづいて最大移動範囲内に oldPosition をクリップ
        const { bounds } = this.state;
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const maxX = Math.max(0, bounds.right - vw / 2);
        const maxY = Math.max(0, bounds.bottom - vh / 2);
        const newX = Math.max(-maxX, Math.min(maxX, oldPosition.x));
        const newY = Math.max(-maxY, Math.min(maxY, oldPosition.y));

        // オートプレイ中であれば再度速度ベクトルをセット、そうでなければマウス操作に戻す
        if (this.state.autoplay.isPlaying) {
          this.state.autoplay.position = { x: newX, y: newY };
          const speed = this.config.movement.autoplaySpeed;
          this.state.autoplay.velocity = {
            x: Math.cos(this.state.autoplay.angle) * speed,
            y: Math.sin(this.state.autoplay.angle) * speed
          };
        } else {
          this.state.movement.currentPosition = { x: newX, y: newY };
          this.state.movement.velocity = { x: 0, y: 0 };
        }

        // 再度アニメーションを開始
        this.startAnimation();
      }, 200);
    }

    /**
     * このクラスで作られたイベントリスナーやアニメーションを解放するためのメソッド。
     * ページ遷移時などに呼ぶ想定。
     */
    destroy() {
      if (this.state.rafId) {
        cancelAnimationFrame(this.state.rafId);
      }
      if (this.resizeTimeout) {
        clearTimeout(this.resizeTimeout);
      }
      if (this.pauseObserver) {
        this.pauseObserver.disconnect();
        this.pauseObserver = null;
      }
      if (!this.isTouchDevice) {
        window.removeEventListener("mousemove", this.boundHandleMouseMove);
      }
      window.removeEventListener("resize", this.boundHandleResize);

      if (this.elementExists.playbackControl) {
        this.elements.playbackControl.removeEventListener("click", this.boundHandlePlaybackControl);
      }

      this.cardElements.forEach((card) => card.element.remove());
    }

    /**
     * 配列の要素をランダムに並び替えるユーティリティ。
     * フィッシャー・イェーツのアルゴリズム。
     */
    shuffleArray(array) {
      for (let i = array.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [array[i], array[j]] = [array[j], array[i]];
      }
      return array;
    }

    /**
     * カードの要素が画面中央からどの程度離れているかを計算する。
     * @param {*} cardRect カード要素の BoundingClientRect
     */
    calculateDistanceFromCenter(cardRect) {
      const cardCenterX = cardRect.left + cardRect.width / 2;
      const cardCenterY = cardRect.top + cardRect.height / 2;
      const relX = (cardCenterX - this.state.viewportCenter.x) / this.state.viewportCenter.x;
      const relY = (cardCenterY - this.state.viewportCenter.y) / this.state.viewportCenter.y;
      // 距離を[0,1]に収めたいので、画面中心からの相対座標の長さを計算し、最大1までクリップ
      return Math.min(1, Math.sqrt(relX * relX + relY * relY));
    }

    /**
     * カード要素(aタグ)を生成する。
     * テキストや縦書き/横書きの幅や高さを設定し、背景色のランダム設定なども適用。
     * @param {string} text カードに表示する文字列
     * @param {string} permalink カードのリンク先。省略時は"#"
     */
    createCardElement(text, permalink) {
      const { maxCardPadding, minCardPadding, minSizeRatio, maxSizeRatio } = this.config;
      const element = document.createElement("a");
      element.classList.add("card", "hover-textcolor-primarysub")
      element.style.padding = Math.random() * (maxCardPadding - minCardPadding) + minCardPadding + "rem";
      element.href = permalink || "#";
      hoverDynamic(element);

      const rand = Math.random();
      const { ratio, maxRem, remPerChar } = this.config.verticalText;
      const textLength = text.length || 0;

      if (rand < ratio) {
        // ratio 以下なら縦書きカードにする
        element.classList.add("card-vertical");

        const dynamicHeightRem = Math.min(maxRem, textLength * remPerChar)
        if(textLength < dynamicHeightRem) {
          element.style.whiteSpace = 'nowrap';
        } else {
          element.style.height = dynamicHeightRem + (Math.random() * (maxSizeRatio - minSizeRatio) + minSizeRatio) + "rem";
        }
      } else {
        // それ以外は横書き
        const { maxRem, remPerChar } = this.config.horizontalText;
        const dynamicWidthRem = Math.min(maxRem, textLength * remPerChar)
        if(textLength < dynamicWidthRem) {
          element.style.whiteSpace = 'nowrap';
        } else {
          element.style.width = dynamicWidthRem + (Math.random() * (maxSizeRatio - minSizeRatio) + minSizeRatio) + "rem";
        }
      }

      // テキストを入れる
      element.innerHTML = text;

      // transformを初期化（中央基準にしておく）
      element.style.transform = "translate(-50%, -50%)";

      // カード要素の情報を返す
      return {
        element,
        width: 0,
        height: 0,
        position: { x: 0, y: 0 }
      };
    }
  }

  // .scattered-cards-container がある場合にインスタンス生成
  const c = document.querySelector(".scattered-cards-container");
  if(c) {
    new ScatteredCardsManager();
    if(FONTPLUS) FONTPLUS.reload();
  }
}